import React, { useState, useEffect } from "react";
import apiClient from "../../../AppClient";
import { Container, Form, Button, Table, Alert, Spinner, Modal } from "react-bootstrap";

const InstallmentsReport = () => {
  const [installments, setInstallments] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [customerFilter, setCustomerFilter] = useState("");
  const [dueDateFilter, setDueDateFilter] = useState("");
  const [isPaidFilter, setIsPaidFilter] = useState(0);

  // Fetch all installments
  const fetchInstallments = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/transactions/installments/", {
        params: {
          customer: customerFilter,
          is_paid: isPaidFilter,
          keyword: dueDateFilter,
          // page: 0,
          order_type: "desc",
          order_by: "due_date",
        },
      });
      setInstallments(response.data.installments);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في جلب الأقساط.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch installment details by ID
  const fetchInstallmentDetails = async (id) => {
    try {
      const response = await apiClient.get(`/transactions/installments/${id}/`);
      setSelectedInstallment(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في جلب تفاصيل القسط.");
    }
  };

  // Filter installments based on customer, due date, and payment status
  const filteredInstallments = installments.filter((installment) => {
    const matchesCustomer = customerFilter
      ? installment.customer.toLowerCase().includes(customerFilter.toLowerCase())
      : true;

    const matchesDueDate = dueDateFilter
      ? installment.due_date.includes(dueDateFilter)
      : true;

    const matchesPaymentStatus = isPaidFilter
      ? installment.is_paid === isPaidFilter
      : true;

    return matchesCustomer && matchesDueDate && matchesPaymentStatus;
  });

  // Open details modal
  const viewInstallmentDetails = (id) => {
    fetchInstallmentDetails(id);
    setShowDetails(true);
  };

  useEffect(() => {
    fetchInstallments();
  }, [customerFilter, dueDateFilter, isPaidFilter]);

  return (
    <Container style={{ 
      direction: "rtl", 
      textAlign: "right", 
      padding: "20px", 
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      color: "black", 
      backgroundColor: "white" 
    }}>
      <h1 style={{ color: "darkgreen", marginBottom: "20px" }}>تقرير الأقساط</h1>

      {/* Filters */}
      <div style={{ display: "flex", marginBottom: "20px", gap: "10px" }}>
        <Form.Control
          type="text"
          placeholder="اسم العميل"
          value={customerFilter}
          onChange={(e) => setCustomerFilter(e.target.value)}
          style={{ maxWidth: "250px" }}
        />

        <Form.Control
          type="date"
          value={dueDateFilter}
          onChange={(e) => setDueDateFilter(e.target.value)}
          style={{ maxWidth: "250px", color: dueDateFilter ? "#000" : "#aaa" }}
        />

        <Form.Control
          as="select"
          value={isPaidFilter}
          onChange={(e) => setIsPaidFilter(e.target.value)}
          style={{ maxWidth: "150px" }}
        >
          <option value="0">غير مدفوعة</option>
          <option value="1">مدفوعة</option>
        </Form.Control>

        <Button 
          variant="outline-secondary" 
          onClick={() => {
            setCustomerFilter("");
            setDueDateFilter("");
            setIsPaidFilter("0");
          }}
          style={{ border: "1px solid darkgreen", color: "darkgreen" }}
        >
          مسح الترتيب
        </Button>
      </div>

      {/* Error Message */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status" style={{ color: "darkgreen" }}>
            <span className="visually-hidden">جاري التحميل...</span>
          </Spinner>
        </div>
      ) : (
        /* Installments Table */
        <Table hover responsive>
          <thead style={{ backgroundColor: "darkgreen", color: "white" }}>
            <tr>
              <th>#</th>
              <th>اسم العميل</th>
              <th>تاريخ الاستحقاق</th>
              <th>المبلغ المتبقي</th>
              <th>حالة الدفع</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {filteredInstallments.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">لم يتم العثور على أقساط</td>
              </tr>
            ) : (
              filteredInstallments.map((installment, index) => (
                <tr key={installment.id}>
                  <td>{index + 1}</td>
                  <td>{installment.customer}</td>
                  <td>{installment.due_date}</td>
                  <td>{installment.amount}</td>
                  <td>{installment.is_paid ? "مدفوعة" : "غير مدفوعة"}</td>
                  <td>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => viewInstallmentDetails(installment.id)}
                      style={{ border: "1px solid darkgreen", color: "darkgreen" }}
                    >
                      عرض التفاصيل
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* Details Modal */}
      <Modal show={showDetails} onHide={() => setShowDetails(false)}>
        <Modal.Header closeButton style={{ backgroundColor: "darkgreen", color: "white" }}>
          <Modal.Title>تفاصيل القسط</Modal.Title>
        </Modal.Header>
        {selectedInstallment && (
          <Modal.Body>
            <p><strong>المعرف:</strong> {selectedInstallment.id}</p>
            <p><strong>اسم العميل:</strong> {selectedInstallment.customer?.name || "لا يوجد"}</p>
            <p><strong>المبلغ:</strong> {selectedInstallment.amount}</p>
            <p><strong>تاريخ الاستحقاق:</strong> {selectedInstallment.due_date}</p>
            <p><strong>حالة الدفع:</strong> {selectedInstallment.is_paid ? "مدفوعة" : "غير مدفوعة"}</p>
            <p><strong>ملاحظات:</strong> {selectedInstallment.notes || "لا توجد ملاحظات"}</p>
            <p><strong>التاريخ:</strong> {selectedInstallment.created_at.split("T")[0]}</p>
            <p><strong>الوقت:</strong> {selectedInstallment.created_at.split("T")[1]}</p>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDetails(false)}
            style={{ backgroundColor: "darkgreen", borderColor: "darkgreen" }}
          >
            إغلاق
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default InstallmentsReport;
